import React, { useState } from "react";
import { Loader2, Megaphone, Send, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { PRAYER_ACCENT, fill } from "@/lib/prayers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// « Partager en story » : visuel généré côté serveur (demande / exaucée / appel à la prière) → aperçu → POST /stories.
export const PrayerStoryShare = ({ p, variant = "outline" }) => {
  const { t, user, config } = useApp();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [imgReady, setImgReady] = useState(false);
  if (!user || config?.settings?.features?.prayer_story === false) return null;
  const rally = !p.is_mine;
  if (rally && (p.author?.anonymous || p.is_answered)) return null;

  const generate = async () => {
    setBusy(true);
    setImgReady(false);
    try {
      const { data } = await api.post(`/prayers/${p.id}/story-image`);
      setPreview(data);
    } catch (e) { toast.error(e?.friendlyMessage || t("prayers.story_error", "Impossible de générer le visuel pour le moment.")); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!preview) return;
    setPublishing(true);
    try {
      await api.post("/stories", { media_type: "image", media_url: preview.media_url });
      window.dispatchEvent(new Event("yalola:story_created"));
      toast.success(t("prayers.story_published", "Story publiée 🙏 Visible 24 h par tes abonnés."));
      setPreview(null);
    } catch (e) { toast.error(e?.friendlyMessage || t("prayers.story_error", "Impossible de générer le visuel pour le moment.")); }
    finally { setPublishing(false); }
  };

  const note = preview?.kind === "answered" ? t("prayers.story_note_answered", "Ton témoignage sera visible 24 h par tes abonnés.")
    : preview?.kind === "rally" ? fill(t("prayers.story_note_rally", "Tu appelles tes abonnés à prier pour {name} (24 h)."), { name: (p.author?.name || "").split(" ")[0] })
      : t("prayers.story_note_request", "Ta demande sera visible 24 h par tes abonnés, avec un appel à prier.");

  return (
    <>
      <Button size="sm" variant={variant} className="rounded-full" disabled={busy} onClick={(e) => { e.stopPropagation(); generate(); }} data-testid={`prayer-story-share-${p.id}`}>
        {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : rally ? <Megaphone size={14} className="mr-1" /> : <Share2 size={14} className="mr-1" />}
        {rally ? t("prayers.story_rally", "Appeler à la prière") : t("prayers.story_share", "Partager en story")}
      </Button>
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o && !publishing) setPreview(null); }}>
        <DialogContent className="sm:max-w-[420px]" data-testid={`prayer-story-preview-${p.id}`}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>{t("prayers.story_preview_title", "Aperçu de la story")}</DialogTitle>
            <DialogDescription data-testid={`prayer-story-note-${p.id}`}>{note}</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="relative w-full rounded-2xl overflow-hidden" style={{ aspectRatio: "9 / 16", background: "#0E1A14" }}>
                {!imgReady && <div className="absolute inset-0 grid place-items-center"><Loader2 className="animate-spin" color="#E0A63C" /></div>}
                <img src={resolveMediaUrl(preview.media_url)} alt="" className="w-full h-full" style={{ objectFit: "cover", opacity: imgReady ? 1 : 0, transition: "opacity .3s" }} onLoad={() => setImgReady(true)} onError={() => { setImgReady(true); toast.error(t("prayers.story_error", "Impossible de générer le visuel pour le moment.")); }} data-testid={`prayer-story-image-${p.id}`} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" className="rounded-full" disabled={publishing} onClick={() => setPreview(null)} data-testid={`prayer-story-cancel-${p.id}`}>{t("common.cancel", "Annuler")}</Button>
                <Button className="rounded-full" style={{ background: PRAYER_ACCENT, color: "#fff" }} disabled={publishing} onClick={publish} data-testid={`prayer-story-publish-${p.id}`}>
                  {publishing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} {t("prayers.publish_story", "Publier en story")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
